import { useMutation, type UseMutationOptions } from '@tanstack/react-query';

import {
  postV1AddressSuggest,
  postV1IdentityCurrentRequestBusinessPartner,
  postV1RequestBusinessPartnerByIdAccept,
  postV1RequestBusinessPartnerByIdDecline,
  postV1RequestBusinessPartnerByIdRegistrationToken,
  postV1RequestBusinessPartnerDivision,
  postV1RequestBusinessPartnerProxy,
  putV1RequestBusinessPartnerById,
  type AddressSuggestionListBody,
  type AddressSuggestPostBody,
  type RequestBusinessPartnerBody,
  type RequestBusinessPartnerDeclineReasonEnum,
  type RequestBusinessPartnerDivisionPostBody,
  type RequestBusinessPartnerEndCustomerPostBody,
  type RequestBusinessPartnerPatchBody,
  type RequestBusinessPartnerProxyPostBody,
} from '../../api/generated/pm';
import { invalidateCustomerAgreementLists } from '../Customers/customers.queries';
import {
  invalidateCurrentBusinessPartnerDivisionRequests,
  invalidateCurrentBusinessPartnerDivisions,
} from '../Divisions/divisions.queries';

/**
 * Mutation hooks for Business Partner Requests: creation (self-service,
 * Division, Dealer proxy), review (update, accept, decline), the registration
 * token for the not-yet-signed-in accept flow, and address suggestion. See
 * `../Settings/IdentityProviders/identityProviders.mutations.ts` for the pattern.
 */

/** Self-service: request a new root Organization for the current identity. */
export const useCreateCompany = () =>
  useMutation({
    mutationFn: async (payload: RequestBusinessPartnerEndCustomerPostBody) => {
      await postV1IdentityCurrentRequestBusinessPartner({ body: payload, throwOnError: true });
    },
  });

/** Request a new Division of the current Business Partner. */
export const useCreateRequestBusinessPartner = () =>
  useMutation({
    mutationFn: async (payload: RequestBusinessPartnerDivisionPostBody) => {
      await postV1RequestBusinessPartnerDivision({ body: payload, throwOnError: true });
    },
    onSuccess: () => {
      // Division creation takes a few seconds to be visible, so we need to wait before invalidating the queries
      // TODO: Remove this after the backend is updated to return the new division immediately
      setTimeout(() => {
        invalidateCurrentBusinessPartnerDivisions();
        invalidateCurrentBusinessPartnerDivisionRequests();
      }, 10000);
    },
  });

/**
 * Dealer proxy: request a new Customer on its behalf. Resolves with the created
 * request on 201; a 202 means the request was accepted for asynchronous
 * processing and carries no body, so it resolves with `undefined`.
 */
export const useCreateRequestBusinessPartnerProxy = () =>
  useMutation({
    mutationFn: async (
      payload: RequestBusinessPartnerProxyPostBody
    ): Promise<RequestBusinessPartnerBody | undefined> => {
      const { data, response } = await postV1RequestBusinessPartnerProxy({
        body: payload,
        throwOnError: true,
      });
      return response.status === 201 ? (data as RequestBusinessPartnerBody) : undefined;
    },
    onSuccess: () => {
      invalidateCustomerAgreementLists();
    },
  });

/**
 * Resubmit the reviewed request's company details. Resolves with the updated
 * request on 200; a 202 means the update was accepted for asynchronous
 * processing and carries no body, so it resolves with `undefined`. The
 * `If-Match` header comes from the shared interceptor (same path as the GET).
 */
export const useUpdateRequestBusinessPartner = () =>
  useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: RequestBusinessPartnerPatchBody;
    }): Promise<RequestBusinessPartnerBody | undefined> => {
      const { data, response } = await putV1RequestBusinessPartnerById({
        path: { id },
        body,
        throwOnError: true,
      });
      return response.status === 200 ? (data as RequestBusinessPartnerBody) : undefined;
    },
  });

export const useAcceptRequestBusinessPartner = () =>
  useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await postV1RequestBusinessPartnerByIdAccept({ path: { id }, throwOnError: true });
    },
  });

export const useDeclineRequestBusinessPartner = () =>
  useMutation({
    mutationFn: async ({
      id,
      declineReason,
    }: {
      id: string;
      declineReason: RequestBusinessPartnerDeclineReasonEnum;
    }) => {
      await postV1RequestBusinessPartnerByIdDecline({
        path: { id },
        body: { declineReason },
        throwOnError: true,
      });
    },
  });

/** Token that lets a not-yet-registered reviewer sign up and land on the accepted request. */
export const useRequestBusinessPartnerRegistrationToken = () =>
  useMutation({
    mutationFn: async (id: string) => {
      const { data } = await postV1RequestBusinessPartnerByIdRegistrationToken({
        path: { id },
        throwOnError: true,
      });
      return data;
    },
  });

export const useSuggestAddress = (
  options?: Omit<
    UseMutationOptions<AddressSuggestionListBody, Error, AddressSuggestPostBody>,
    'mutationFn'
  >
) =>
  useMutation({
    ...options,
    mutationFn: async (payload: AddressSuggestPostBody) => {
      const { data } = await postV1AddressSuggest({ body: payload, throwOnError: true });
      return data;
    },
  });
